import { useCallback, useMemo } from 'react';
import { css } from '@emotion/react';
import { InsuranceDetailQueries } from '@frontend/api-insurance-detail';
import { useTranslation } from '@frontend/i18n';
import { useAppScopeStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { BannerNotification, Button, SpinningLoader, Text } from '@frontend/design-system';
import { useInsuranceContext } from '../providers/insurance-provider';
import { useInsurancePayerConnectionShallowStore } from '../stores/use-insurance-payer-connection-store';
import { insuranceTabTrackingIds } from '../tracking-ids';

interface InsuranceCardBannerNotificationProps {
  enhancedVerificationInprogress: boolean;
  isEligibleForWebsiteResp: boolean;
  locationId: string;
  payerID?: string;
  onRetryVerification: () => void;
}

export const InsuranceCardBannerNotification = ({
  enhancedVerificationInprogress,
  isEligibleForWebsiteResp,
  locationId,
  payerID,
  onRetryVerification,
}: InsuranceCardBannerNotificationProps) => {
  const { t } = useTranslation();

  const { setPayerConnectionPayerID, setPayerConnectionPayerWebsiteID } = useInsurancePayerConnectionShallowStore(
    'setPayerConnectionPayerID',
    'setPayerConnectionPayerWebsiteID'
  );

  const { payersList } = useInsuranceContext();

  const { selectedLocationIds } = useAppScopeStore();

  const isSingleLocation = selectedLocationIds.length === 1;

  const { data: payerConnectionDetails, isLoading: isPayerConnectionDetailsLoading } =
    InsuranceDetailQueries.useGetPayerConnectionDetails(locationId, {
      enabled: isEligibleForWebsiteResp && !!payerID && !enhancedVerificationInprogress,
    });

  const { hasConnectionInitiatedWithPayer, payerWebsiteId, hasPayerConnectedWithWebsite } = useMemo(() => {
    const payerWebsiteIdDetails = payersList?.find(
      (payer) => payer.id === payerID || payer.alternatePayerIds?.includes(payerID || '')
    )?.websiteId;

    const payerConnection = payerConnectionDetails?.connections?.find(
      (connection) =>
        connection.payerId === payerID ||
        connection.alternativePayerIds?.includes(payerID || '') ||
        connection.websiteId === payerWebsiteIdDetails
    );

    return {
      hasConnectionInitiatedWithPayer: !!payerConnection?.id && !!payerConnection?.websiteId,
      hasPayerConnectedWithWebsite: !!payerConnection?.websiteId && payerConnection?.status === 'connected',
      payerWebsiteId: payerWebsiteIdDetails,
    };
  }, [payerConnectionDetails?.connections, payerID, payersList]);

  const navigateToPayerConnectionManagement = useCallback(() => {
    setPayerConnectionPayerID(payerID || '');
    setPayerConnectionPayerWebsiteID(payerWebsiteId || '');
    if (isSingleLocation) {
      window.location.hash = 'settings/insurance/payer-connection-management';
    } else {
      window.location.hash = `settings/insurance/payer-connection-management/${locationId}`;
    }
  }, [
    isSingleLocation,
    locationId,
    payerID,
    payerWebsiteId,
    setPayerConnectionPayerID,
    setPayerConnectionPayerWebsiteID,
  ]);

  const payerNotConnectedMessage = useMemo(
    () => (
      <>
        <Text>
          {t('Payer credentials are missing. Insurance eligibility results are limited until they are connected.')}
        </Text>
        <Button
          css={{ marginTop: theme.spacing(1), padding: 0 }}
          variant='tertiary'
          trackingId={insuranceTabTrackingIds.addPayerConnectionButton}
          onClick={navigateToPayerConnectionManagement}
        >
          {t('Connect Payers')}
        </Button>
      </>
    ),
    [t, navigateToPayerConnectionManagement]
  );

  const payerEligibleForWebsiteResponseMessage = useMemo(
    () => (
      <>
        <Text>
          {hasPayerConnectedWithWebsite
            ? t(
                'Currently showing EDI response. Your payer credentials are connected. Retry to view the full website report.'
              )
            : t('Currently showing EDI response. Connect your payer credentials to view the full website report.')}
        </Text>
        {hasPayerConnectedWithWebsite ? (
          <Button
            css={{ marginTop: theme.spacing(1), padding: 0 }}
            variant='tertiary'
            trackingId={insuranceTabTrackingIds.addPayerConnectionButton}
            onClick={onRetryVerification}
            iconName='update'
          >
            {t('Retry')}
          </Button>
        ) : (
          <Button
            css={{ marginTop: theme.spacing(1), padding: 0 }}
            variant='tertiary'
            trackingId={insuranceTabTrackingIds.addPayerConnectionButton}
            onClick={navigateToPayerConnectionManagement}
            iconName='plus'
          >
            {t('Add Payer Credential')}
          </Button>
        )}
      </>
    ),
    [t, hasPayerConnectedWithWebsite, onRetryVerification, navigateToPayerConnectionManagement]
  );

  const hasPayerConnectionInitiated = hasConnectionInitiatedWithPayer || hasPayerConnectedWithWebsite;

  if (isPayerConnectionDetailsLoading) {
    return <SpinningLoader size='small' />;
  }

  if (enhancedVerificationInprogress) {
    return (
      <BannerNotification
        css={commonBannerStyles}
        status='warn'
        message={t("We're currently retrieving your report. This may take 1-2 minutes.")}
      />
    );
  }

  if (isEligibleForWebsiteResp) {
    return (
      <BannerNotification
        css={commonBannerStyles}
        status={hasPayerConnectionInitiated ? 'warn' : 'error'}
        message={hasPayerConnectionInitiated ? payerEligibleForWebsiteResponseMessage : payerNotConnectedMessage}
      />
    );
  }

  return null;
};

const commonBannerStyles = css({ margin: theme.spacing(2, 0), alignItems: 'flex-start' });
